/* ---------------------------------------------------------------------------------------------------------------------
   AP CART RECENTLY VIEWED
   ---------------------------------------------------------------------------------------------------------------------
   Drives the "Cart" / "Recently viewed" tabs inside the cart drawer (sections/cart-drawer.liquid).

   Deliberately standalone: the theme's own <recently-viewed-products> calls section.remove() when there
   is no history, which would delete the whole cart drawer. This element only ever hides its own tab.

   Kept as an asset rather than inline: the drawer renders on every page AND is bundled into every
   cart/add.js | cart/change.js response, so inlining shipped these bytes on every page view and every
   cart mutation. As an asset it is fetched once and served from cache afterwards.
--------------------------------------------------------------------------------------------------------------------- */
(() => {
  // Fetched markup is reused for the rest of the page session: the drawer is rebuilt on every cart
  // update, and re-requesting an identical list would be pure waste.
  window.apCartRecentCache = window.apCartRecentCache || {};

  if (!window.customElements.get('ap-cart-recently-viewed')) {
    class ApCartRecentlyViewed extends HTMLElement {
      connectedCallback() {
        // Deliberately NO network here. The cart drawer is rendered on every page (it lives in the
        // overlay group) and is rebuilt on every cart change, so fetching on connect would hit the
        // Section Rendering API on each page load and each add-to-cart. Whether the tab is worth
        // showing is answered by localStorage alone — synchronous and free.
        if (this.ids.length === 0) {
          this.tab?.setAttribute('hidden', '');
        }
      }

      get tab() {
        return this.closest('cart-drawer')?.querySelector('[data-ap-tab="recent"]');
      }

      // Give up on this tab. If the visitor is standing on it (they clicked it to trigger the load),
      // send them back to the cart first — otherwise the tab vanishes and they are left staring at an
      // empty panel with no obvious way out.
      dropTab() {
        const tab = this.tab;
        const wasActive = tab?.getAttribute('aria-selected') === 'true';

        tab?.setAttribute('hidden', '');

        if (wasActive) {
          this.closest('cart-drawer')?.querySelector('[data-ap-tab="cart"]')?.click();
        }
      }

      get ids() {
        try {
          const stored = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
          return Array.from(new Set(stored)).slice(0, parseInt(this.dataset.limit, 10) || 5);
        } catch (e) {
          return [];
        }
      }

      // The cards are rendered by the SAME section as the cart recommendations, so horizontal-product
      // derives its ids from the same section.id — a product shown in both panels ends up with two
      // elements sharing one id. DialogElement binds by [aria-controls="<id>"], so a single click
      // would open both quick-buy drawers (the popup appearing twice). Re-key ours for this panel.
      // Runs on the detached DOMParser document, where custom elements are not upgraded yet.
      scopeIds(fragment) {
        fragment.querySelectorAll('[id]').forEach((element) => {
          const id = element.id;
          if (!/^(quick-buy-horizontal|complementary-product)-/.test(id)) return;

          const scopedId = `ap-recent-${id}`;
          fragment
            .querySelectorAll(`[aria-controls="${id}"]`)
            .forEach((reference) => reference.setAttribute('aria-controls', scopedId));
          fragment
            .querySelectorAll(`[form="${id}"]`)
            .forEach((reference) => reference.setAttribute('form', scopedId));

          element.id = scopedId;
        });
      }

      // Called the first time the visitor actually opens the tab.
      async load() {
        if (this.loaded) return;
        this.loaded = true;

        const ids = this.ids;
        if (ids.length === 0) {
          this.dropTab();
          return;
        }

        const query = ids.map((id) => `id:${id}`).join(' OR ');

        if (window.apCartRecentCache[query]) {
          this.innerHTML = window.apCartRecentCache[query];
          return;
        }

        this.setAttribute('aria-busy', 'true');
        this.innerHTML = '<div class="ap-cart-recent__loading" aria-hidden="true"></div>';

        try {
          const response = await fetch(`${this.dataset.url}${encodeURIComponent(query)}`, { priority: 'low' });
          if (!response.ok) throw new Error(response.statusText);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = doc.querySelector('#ap-cart-recent-results');

          if (results && results.children.length > 0) {
            this.scopeIds(results);
            window.apCartRecentCache[query] = results.innerHTML;
            this.innerHTML = results.innerHTML;
          } else {
            this.replaceChildren();
            this.dropTab();
          }
        } catch (e) {
          // Never let this break the cart: drop the tab and fall back to a plain drawer.
          this.loaded = false;
          this.replaceChildren();
          this.dropTab();
        } finally {
          this.removeAttribute('aria-busy');
        }
      }
    }

    window.customElements.define('ap-cart-recently-viewed', ApCartRecentlyViewed);
  }

  // Delegated so it survives the drawer being re-rendered on every cart update.
  if (!window.apCartTabsBound) {
    window.apCartTabsBound = true;

    const activate = (drawer, active) => {
      drawer.querySelectorAll('[data-ap-tab]').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.apTab === active));
      });

      drawer.querySelectorAll('[data-ap-panel]').forEach((panel) => {
        panel.toggleAttribute('hidden', panel.dataset.apPanel !== active);
      });

      // The free-shipping bar and the Total / Checkout footer stay put on both tabs: hiding them
      // reflowed everything below the header, and keeping checkout reachable is the safer default.
      drawer.querySelectorAll('.ap-cart-tabs').forEach((group) => {
        group.dataset.active = active;
      });

      // Lazy: the products are only requested once the visitor opens the tab. The panel is unhidden
      // above before this runs, so the injected images can resolve their own native lazy loading.
      if (active === 'recent') {
        drawer.querySelectorAll('ap-cart-recently-viewed').forEach((element) => element.load());
      }
    };

    document.addEventListener('click', (event) => {
      const tab = event.target?.closest?.('[data-ap-tab]');
      if (!tab) return;

      const drawer = tab.closest('cart-drawer');
      if (drawer) activate(drawer, tab.dataset.apTab);
    });

    // Adding from the "Recently viewed" tab must snap back to the cart, like the reference store does.
    ['variant:add', 'cart:change'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        document.querySelectorAll('cart-drawer').forEach((drawer) => {
          setTimeout(() => activate(drawer, 'cart'), 0);
        });
      });
    });
  }
})();
